import React, { useEffect, useMemo, useState } from "react";
import "./CSS/SareeDetails.css";
import { backend_url } from "../App";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { exportCsv } from "../utils/exportCsv";
import { menuItems } from "../Constants/menuItems";

/* Saree subcategory labels for pretty display */
const SAREE_SUBCATS = (menuItems.find((m) => m.value === "sarees")?.dropdown || []);
const subcatLabel = (value) =>
  SAREE_SUBCATS.find((s) => s.value?.toLowerCase() === value?.toLowerCase())?.label || value || "—";

/* Saree stock thresholds (mirrors Stock page): 0 → Out, 1–2 → Low, 3+ → In-Stock */
const getAutoStatus = (qty) => {
  const n = Number(qty);
  if (isNaN(n) || n === 0) return "Out of Stock";
  if (n < 3) return "Low Stock";
  return "In-Stock";
};

const badgeClass = (status) => {
  if (status === "In-Stock") return "instock";
  if (status === "Low Stock") return "lowstock";
  return "outstock";
};

const STATUS_TABS = ["All", "In-Stock", "Low Stock", "Out of Stock"];

/* Stock-check persistence (browser) */
const CHECK_KEY = "sd_stock_check_v1";
const pid = (p) => p._id || `${p.unique_id}::${p.name}`;
const loadCheck = () => {
  try {
    const raw = localStorage.getItem(CHECK_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { startedAt: null, marks: {} };
};

const SareeDetails = () => {
  const [sarees, setSarees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null); // { product, index }

  /* Stock check state */
  const [checkMode, setCheckMode] = useState(false);
  const [check, setCheck] = useState(loadCheck); // { startedAt, marks: { [pid]: 'present'|'missing' } }
  const [uncheckedOnly, setUncheckedOnly] = useState(false);

  /* persist check to localStorage */
  useEffect(() => {
    try { localStorage.setItem(CHECK_KEY, JSON.stringify(check)); } catch { /* ignore */ }
  }, [check]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${backend_url}/allproducts`);
        const data = await res.json();
        setSarees(data.filter((p) => p.category?.toLowerCase() === "sarees"));
      } catch {
        toast.error("Failed to load sarees");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Group sarees by unique_id */
  const groups = useMemo(() => {
    const map = new Map();
    sarees.forEach((p) => {
      const id = (p.unique_id || "—").trim();
      if (!map.has(id)) map.set(id, []);
      map.get(id).push(p);
    });
    return Array.from(map.entries())
      .map(([id, items]) => {
        const totalQty = items.reduce((sum, p) => sum + (Number(p.quantity) || 0), 0);
        return {
          id,
          items,
          count: items.length,
          totalQty,
          status: getAutoStatus(totalQty),
        };
      })
      .sort((a, b) => b.count - a.count || a.id.localeCompare(b.id));
  }, [sarees]);

  /* Apply search + status + duplicates filters */
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (duplicatesOnly && g.count < 2) return false;
      if (activeTab !== "All" && g.status !== activeTab) return false;
      if (uncheckedOnly && !g.items.some((p) => !check.marks[pid(p)])) return false;
      if (q) {
        const inId = g.id.toLowerCase().includes(q);
        const inName = g.items.some(
          (p) =>
            p.name?.toLowerCase().includes(q) ||
            p.subcategory?.toLowerCase().includes(q)
        );
        if (!inId && !inName) return false;
      }
      return true;
    });
  }, [groups, search, activeTab, duplicatesOnly, uncheckedOnly, check]);

  /* Stats */
  const stats = useMemo(() => {
    return {
      totalSarees: sarees.length,
      uniqueIds: groups.length,
      duplicateIds: groups.filter((g) => g.count > 1).length,
      outOfStock: groups.filter((g) => g.status === "Out of Stock").length,
    };
  }, [sarees, groups]);

  /* ── Stock check helpers ── */
  const markOf = (p) => check.marks[pid(p)]; // 'present' | 'missing' | undefined

  const setMark = (p, status) => {
    setCheck((prev) => {
      const key = pid(p);
      const marks = { ...prev.marks };
      if (marks[key] === status) delete marks[key]; // click same → clear
      else marks[key] = status;
      return {
        startedAt: prev.startedAt || new Date().toISOString(),
        marks,
      };
    });
  };

  const markGroup = (g, status) => {
    setCheck((prev) => {
      const marks = { ...prev.marks };
      g.items.forEach((p) => { marks[pid(p)] = status; });
      return { startedAt: prev.startedAt || new Date().toISOString(), marks };
    });
  };

  const resetCheck = () => {
    if (!window.confirm("Clear all present/missing marks and start a fresh stock check?")) return;
    setCheck({ startedAt: null, marks: {} });
    toast.info("Stock check reset");
  };

  const checkStats = useMemo(() => {
    let present = 0, missing = 0;
    sarees.forEach((p) => {
      const m = check.marks[pid(p)];
      if (m === "present") present++;
      else if (m === "missing") missing++;
    });
    const total = sarees.length;
    return { present, missing, remaining: total - present - missing, total };
  }, [sarees, check]);

  /* per-group check counts */
  const groupCheck = (g) => {
    let present = 0, missing = 0;
    g.items.forEach((p) => {
      const m = check.marks[pid(p)];
      if (m === "present") present++;
      else if (m === "missing") missing++;
    });
    return { present, missing, unchecked: g.count - present - missing };
  };

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  /* Image lightbox */
  const openPreview = (product, index = 0) => setPreview({ product, index });
  const closePreview = () => setPreview(null);
  const previewImages = preview?.product?.images || [];
  const stepPreview = (dir) =>
    setPreview((p) => {
      if (!p) return p;
      const imgs = p.product.images || [];
      if (imgs.length < 2) return p;
      const next = (p.index + dir + imgs.length) % imgs.length;
      return { ...p, index: next };
    });

  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === "Escape") closePreview();
      if (e.key === "ArrowRight") stepPreview(1);
      if (e.key === "ArrowLeft") stepPreview(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);

  const handleExport = () => {
    const header = ["Saree ID", "Sarees Sharing ID", "Name", "Subcategory", "Quantity", "Status"];
    const rows = [];
    filteredGroups.forEach((g) => {
      g.items.forEach((p) => {
        rows.push([
          g.id,
          g.count,
          p.name,
          subcatLabel(p.subcategory),
          p.quantity ?? "",
          getAutoStatus(p.quantity),
        ]);
      });
    });
    exportCsv("saree-details.csv", header, rows);
  };

  /* Export the stock-check result as the history record */
  const handleExportCheck = () => {
    const checkedBy = sessionStorage.getItem("adminName") || "—";
    const when = new Date();
    const stamp =
      `${when.getFullYear()}-${String(when.getMonth() + 1).padStart(2, "0")}-${String(when.getDate()).padStart(2, "0")}`;
    const header = ["Saree ID", "Name", "Type", "System Qty", "Check Result", "Checked By", "Date"];
    const rows = [];
    groups.forEach((g) => {
      g.items.forEach((p) => {
        const m = check.marks[pid(p)];
        rows.push([
          g.id,
          p.name,
          subcatLabel(p.subcategory),
          p.quantity ?? "",
          m === "present" ? "Present" : m === "missing" ? "MISSING" : "Not checked",
          checkedBy,
          stamp,
        ]);
      });
    });
    exportCsv(`saree-stock-check-${stamp}.csv`, header, rows);
    toast.success("Stock check exported");
  };

  return (
    <div className="sd-page">
      {/* Header */}
      <div className="sd-header">
        <div className="sd-title-row">
          <h1 className="sd-title">Saree Details</h1>
          <span className="sd-total-badge">{stats.uniqueIds} IDs · {stats.totalSarees} sarees</span>
          <button
            className={`sd-check-toggle ${checkMode ? "on" : ""}`}
            onClick={() => setCheckMode((v) => !v)}
          >
            {checkMode ? "✓ Stock Check: ON" : "☑ Start Stock Check"}
          </button>
          <button className="sd-export-btn" onClick={handleExport} disabled={!filteredGroups.length}>
            ⬇ Export CSV
          </button>
        </div>
        <p className="sd-subtitle">
          Sarees grouped by their ID — spot duplicate IDs and check store stock at a glance.
        </p>

        <div className="sd-controls-row">
          <div className="sd-tabs">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab}
                className={`sd-tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <label className={`sd-dup-toggle ${duplicatesOnly ? "on" : ""}`}>
            <input
              type="checkbox"
              checked={duplicatesOnly}
              onChange={(e) => setDuplicatesOnly(e.target.checked)}
            />
            Duplicate IDs only
          </label>

          <div className="sd-search-wrap">
            <span className="sd-search-icon">&#128269;</span>
            <input
              type="text"
              placeholder="Search by ID, name or type..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Stock check summary bar */}
        {checkMode && (
          <div className="sd-check-bar">
            <div className="sd-check-progress">
              <div className="sd-check-progress-track">
                <div
                  className="sd-check-progress-fill"
                  style={{
                    width: `${checkStats.total ? ((checkStats.present + checkStats.missing) / checkStats.total) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="sd-check-progress-text">
                {checkStats.present + checkStats.missing} / {checkStats.total} checked
              </span>
            </div>

            <div className="sd-check-counts">
              <span className="sd-check-count present">✓ {checkStats.present} present</span>
              <span className="sd-check-count missing">✗ {checkStats.missing} missing</span>
              <span className="sd-check-count remaining">◻ {checkStats.remaining} left</span>
            </div>

            <label className={`sd-dup-toggle ${uncheckedOnly ? "on" : ""}`}>
              <input
                type="checkbox"
                checked={uncheckedOnly}
                onChange={(e) => setUncheckedOnly(e.target.checked)}
              />
              Unchecked only
            </label>

            <button className="sd-check-export-btn" onClick={handleExportCheck}>
              ⬇ Export Check
            </button>
            <button className="sd-check-reset-btn" onClick={resetCheck}>
              ↺ Reset
            </button>
          </div>
        )}
      </div>

      {/* Stat Cards */}
      <div className="sd-stats-row">
        <div className="sd-stat-card scard-total">
          <div className="sd-stat-icon">&#129527;</div>
          <div className="sd-stat-info">
            <span className="sd-stat-label">Total Sarees</span>
            <span className="sd-stat-value">{stats.totalSarees}</span>
          </div>
        </div>
        <div className="sd-stat-card scard-ids">
          <div className="sd-stat-icon">&#127991;</div>
          <div className="sd-stat-info">
            <span className="sd-stat-label">Unique IDs</span>
            <span className="sd-stat-value">{stats.uniqueIds}</span>
          </div>
        </div>
        <div className="sd-stat-card scard-dup">
          <div className="sd-stat-icon">&#128203;</div>
          <div className="sd-stat-info">
            <span className="sd-stat-label">Duplicate IDs</span>
            <span className="sd-stat-value">{stats.duplicateIds}</span>
          </div>
        </div>
        <div className="sd-stat-card scard-out">
          <div className="sd-stat-icon">&#10060;</div>
          <div className="sd-stat-info">
            <span className="sd-stat-label">Out of Stock</span>
            <span className="sd-stat-value">{stats.outOfStock}</span>
          </div>
        </div>
      </div>

      {/* Groups */}
      {loading ? (
        <div className="sd-empty">Loading sarees…</div>
      ) : filteredGroups.length === 0 ? (
        <div className="sd-empty">
          <div className="sd-empty-icon">&#129527;</div>
          <div>No sarees found</div>
        </div>
      ) : (
        <div className="sd-groups">
          {filteredGroups.map((g) => {
            const isOpen = expanded[g.id];
            const gc = groupCheck(g);
            const allChecked = gc.unchecked === 0;
            const cardCheckCls = checkMode
              ? gc.missing > 0
                ? "check-has-missing"
                : allChecked
                ? "check-done"
                : ""
              : "";
            return (
              <div key={g.id} className={`sd-group-card ${g.count > 1 ? "is-dup" : ""} ${cardCheckCls}`}>
                <button className="sd-group-head" onClick={() => toggle(g.id)}>
                  <div className="sd-group-thumbs">
                    {g.items.slice(0, 3).map((p, i) => (
                      <img
                        key={p._id || i}
                        src={p.images?.[0]}
                        alt={p.name}
                        className="sd-group-thumb"
                        style={{ zIndex: 3 - i }}
                        title="Click to enlarge"
                        onClick={(e) => { e.stopPropagation(); openPreview(p, 0); }}
                      />
                    ))}
                    {g.count > 3 && <span className="sd-group-thumb-more">+{g.count - 3}</span>}
                  </div>

                  <div className="sd-group-idinfo">
                    <span className="sd-group-id">{g.id}</span>
                    <span className="sd-group-meta">
                      {g.count} saree{g.count > 1 ? "s" : ""} under this ID
                    </span>
                  </div>

                  {g.count > 1 && (
                    <span className="sd-dup-pill" title="Multiple sarees share this ID">
                      ×{g.count} same ID
                    </span>
                  )}

                  <div className="sd-group-qty">
                    <span className="sd-group-qty-value">{g.totalQty}</span>
                    <span className="sd-group-qty-label">in stock</span>
                  </div>

                  <span className={`sd-badge ${badgeClass(g.status)}`}>{g.status}</span>

                  {checkMode && (
                    <span
                      className={`sd-check-chip ${
                        gc.missing > 0 ? "missing" : allChecked ? "done" : "pending"
                      }`}
                      title={`${gc.present} present · ${gc.missing} missing · ${gc.unchecked} unchecked`}
                    >
                      {gc.missing > 0
                        ? `✗ ${gc.missing} missing`
                        : allChecked
                        ? "✓ Checked"
                        : `${gc.present + gc.missing}/${g.count} checked`}
                    </span>
                  )}

                  <span className={`sd-chevron ${isOpen ? "open" : ""}`}>▾</span>
                </button>

                {isOpen && (
                  <div className="sd-group-body">
                    {checkMode && g.count > 1 && (
                      <div className="sd-group-quickmark">
                        <span className="sd-group-quickmark-label">Mark all in this ID:</span>
                        <button className="sd-mark-btn present sm" onClick={() => markGroup(g, "present")}>
                          ✓ All Present
                        </button>
                        <button className="sd-mark-btn missing sm" onClick={() => markGroup(g, "missing")}>
                          ✗ All Missing
                        </button>
                      </div>
                    )}
                    <table className="sd-variant-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Saree</th>
                          <th>Type</th>
                          <th>Quantity</th>
                          <th>Status</th>
                          {checkMode && <th>Stock Check</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {g.items.map((p, idx) => {
                          const mark = markOf(p);
                          return (
                          <tr key={p._id || idx} className={checkMode && mark ? `sd-row-${mark}` : ""}>
                            <td>{idx + 1}</td>
                            <td>
                              <div className="sd-variant-cell">
                                <button
                                  type="button"
                                  className="sd-variant-thumb-btn"
                                  title="Click to enlarge"
                                  onClick={() => openPreview(p, 0)}
                                >
                                  <img
                                    src={p.images?.[0]}
                                    alt={p.name}
                                    className="sd-variant-thumb"
                                  />
                                  <span className="sd-zoom-badge">&#128269;</span>
                                </button>
                                <span className="sd-variant-name">{p.name}</span>
                              </div>
                            </td>
                            <td>
                              <span className="sd-subcat-pill">{subcatLabel(p.subcategory)}</span>
                            </td>
                            <td>
                              <span className="sd-variant-qty">{p.quantity ?? "—"}</span>
                              <span className="sd-variant-scale">Pcs</span>
                            </td>
                            <td>
                              <span className={`sd-badge ${badgeClass(getAutoStatus(p.quantity))}`}>
                                {getAutoStatus(p.quantity)}
                              </span>
                            </td>
                            {checkMode && (
                              <td>
                                <div className="sd-mark-group">
                                  <button
                                    className={`sd-mark-btn present ${mark === "present" ? "active" : ""}`}
                                    onClick={() => setMark(p, "present")}
                                    title="Mark as present in store"
                                  >
                                    ✓ Present
                                  </button>
                                  <button
                                    className={`sd-mark-btn missing ${mark === "missing" ? "active" : ""}`}
                                    onClick={() => setMark(p, "missing")}
                                    title="Mark as missing / not found"
                                  >
                                    ✗ Missing
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Image Lightbox */}
      {preview && (
        <div className="sd-lightbox-overlay" onClick={closePreview}>
          <div className="sd-lightbox" onClick={(e) => e.stopPropagation()}>
            <button className="sd-lightbox-close" onClick={closePreview} title="Close">
              &times;
            </button>

            <div className="sd-lightbox-stage">
              {previewImages.length > 1 && (
                <button
                  className="sd-lightbox-nav prev"
                  onClick={() => stepPreview(-1)}
                  title="Previous"
                >
                  &#8249;
                </button>
              )}

              <img
                src={previewImages[preview.index]}
                alt={preview.product.name}
                className="sd-lightbox-img"
              />

              {previewImages.length > 1 && (
                <button
                  className="sd-lightbox-nav next"
                  onClick={() => stepPreview(1)}
                  title="Next"
                >
                  &#8250;
                </button>
              )}
            </div>

            <div className="sd-lightbox-info">
              <span className="sd-lightbox-id">{preview.product.unique_id}</span>
              <span className="sd-lightbox-name">{preview.product.name}</span>
              <span className="sd-lightbox-sub">
                {subcatLabel(preview.product.subcategory)}
                {previewImages.length > 1 && (
                  <span className="sd-lightbox-count">
                    · {preview.index + 1}/{previewImages.length}
                  </span>
                )}
              </span>

              {/* Mark present / missing from the popup */}
              <div className="sd-lightbox-mark">
                <button
                  className={`sd-mark-btn present ${markOf(preview.product) === "present" ? "active" : ""}`}
                  onClick={() => { setMark(preview.product, "present"); closePreview(); }}
                  title="Mark as present in store"
                >
                  ✓ Present
                </button>
                <button
                  className={`sd-mark-btn missing ${markOf(preview.product) === "missing" ? "active" : ""}`}
                  onClick={() => { setMark(preview.product, "missing"); closePreview(); }}
                  title="Mark as missing / not found"
                >
                  ✗ Missing
                </button>
              </div>
            </div>

            {previewImages.length > 1 && (
              <div className="sd-lightbox-strip">
                {previewImages.map((img, i) => (
                  <img
                    key={i}
                    src={img}
                    alt={`${preview.product.name} ${i + 1}`}
                    className={`sd-lightbox-strip-thumb ${i === preview.index ? "active" : ""}`}
                    onClick={() => setPreview((p) => ({ ...p, index: i }))}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default SareeDetails;
